'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Search, MapPin, Calendar, Users, ChevronDown, Star } from 'lucide-react';

const eventTypes = ['Wedding', 'Birthday', 'Corporate', 'Anniversary', 'Conference', 'Reception'];
const cities = ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Pune', 'Kolkata', 'Jaipur'];

const heroStats = [
{ value: '2,400+', label: 'Premium Venues' },
{ value: '18 Cities', label: 'Pan India' },
{ value: '4.8★', label: 'Avg. Rating' }];


export default function HeroSection() {
  const [location, setLocation] = useState('');
  const [eventType, setEventType] = useState('');
  const [guests, setGuests] = useState('');
  const [date, setDate] = useState('');
  const [locationOpen, setLocationOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);

  return (
    <section className="relative min-h-[92vh] flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1629307095660-d73f0cc2fb03"
          alt="Grand wedding banquet hall with crystal chandeliers and elegant white floral arrangements"
          fill
          priority
          className="object-cover" />
        
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-red-950/30 to-transparent" />
      </div>
      {/* Content */}
      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-10 pt-24 pb-16 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-6 animate-fade-in">
          <Star size={14} className="text-yellow-400 fill-yellow-400" />
          India&apos;s #1 Venue Booking Platform
        </div>

        {/* Headline */}
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-5 leading-tight animate-slide-up">
          Book Your{' '}
          <span className="text-red-400">Dream</span> Venue
        </h1>
        <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-10 font-medium animate-slide-up">
          Discover 2,400+ premium venues for weddings, birthdays, and corporate events — with instant availability and advance booking.
        </p>

        {/* Search Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-4 md:p-5 max-w-4xl mx-auto animate-slide-up">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {/* Location */}
            <div className="relative">
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 text-left px-1">Location</label>
              <button
                onClick={() => {setLocationOpen(!locationOpen);setEventOpen(false);}}
                className="w-full flex items-center gap-2 px-3 py-2.5 border-2 border-gray-100 hover:border-red-300 rounded-xl text-left transition-colors">
                
                <MapPin size={16} className="text-red-500 shrink-0" />
                <span className={`text-sm font-medium flex-1 truncate ${location ? 'text-gray-900' : 'text-gray-400'}`}>
                  {location || 'Select city'}
                </span>
                <ChevronDown size={14} className="text-gray-400 shrink-0" />
              </button>
              {locationOpen &&
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-xl z-50 py-1 max-h-48 overflow-y-auto">
                  {cities?.map((city) =>
                <button
                  key={`city-${city}`}
                  onClick={() => {setLocation(city);setLocationOpen(false);}}
                  className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors">
                  
                      {city}
                    </button>
                )}
                </div>
              }
            </div>

            {/* Event Type */}
            <div className="relative">
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 text-left px-1">Event Type</label>
              <button
                onClick={() => {setEventOpen(!eventOpen);setLocationOpen(false);}}
                className="w-full flex items-center gap-2 px-3 py-2.5 border-2 border-gray-100 hover:border-red-300 rounded-xl text-left transition-colors">
                
                <Calendar size={16} className="text-red-500 shrink-0" />
                <span className={`text-sm font-medium flex-1 truncate ${eventType ? 'text-gray-900' : 'text-gray-400'}`}>
                  {eventType || 'Select type'}
                </span>
                <ChevronDown size={14} className="text-gray-400 shrink-0" />
              </button>
              {eventOpen &&
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-xl z-50 py-1">
                  {eventTypes?.map((type) =>
                <button
                  key={`event-${type}`}
                  onClick={() => {setEventType(type);setEventOpen(false);}}
                  className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors">
                  
                      {type}
                    </button>
                )}
                </div>
              }
            </div>

            {/* Guests */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 text-left px-1">Guests</label>
              <div className="flex items-center gap-2 px-3 py-2.5 border-2 border-gray-100 hover:border-red-300 rounded-xl transition-colors">
                <Users size={16} className="text-red-500 shrink-0" />
                <input
                  type="number"
                  placeholder="No. of guests"
                  value={guests}
                  onChange={(e) => setGuests(e?.target?.value)}
                  className="w-full text-sm font-medium text-gray-900 placeholder-gray-400 outline-none bg-transparent"
                  min={1} />
                
              </div>
            </div>

            {/* Search CTA */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 text-left px-1 opacity-0 select-none">Search</label>
              <Link
                href={`/venue-listing-page${location ? `?city=${location}` : ''}${eventType ? `&type=${eventType}` : ''}`}
                className="w-full btn-primary justify-center py-3 rounded-xl text-sm">
                
                <Search size={16} />
                Search Venues
              </Link>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="flex flex-wrap justify-center gap-6 mt-10">
          {heroStats?.map((stat) =>
          <div key={`hero-stat-${stat?.label}`} className="text-center">
              <div className="text-2xl font-extrabold text-white font-tabular">{stat?.value}</div>
              <div className="text-white/60 text-xs font-medium mt-0.5">{stat?.label}</div>
            </div>
          )}
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-white/40 flex items-start justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/60 rounded-full" />
        </div>
      </div>
    </section>);

}